import { useEffect, useMemo, useRef } from "react";
import { Account } from "@common/types/domains/accounting/account";
import { resolveAccrualLineItemDefaultAccountIds } from "@common/helpers/accrualAccount";
import { useAccountsByEntity } from "@hooks/useAccounts";
import { useMemoByKeys } from "@hooks/useMemoByKeys";
import { AccrualImporterRow, AccrualMode } from "./types";

interface SeedParams {
    rows: AccrualImporterRow[];
    mode: AccrualMode;
    accountsByEntityId: Record<number, Account[]>;
}

interface SeedResult {
    rows: AccrualImporterRow[];
    didChange: boolean;
}

export function seedAccrualDefaultAccounts({
    rows,
    mode,
    accountsByEntityId,
}: SeedParams): SeedResult {
    const defaultsByEntityId = new Map<
        number,
        ReturnType<typeof resolveAccrualLineItemDefaultAccountIds>
    >();

    const getDefaults = (entityId: number) => {
        const cached = defaultsByEntityId.get(entityId);
        if (cached) {
            return cached;
        }
        const resolved = resolveAccrualLineItemDefaultAccountIds({
            mode: mode === "bill" ? "bills" : "invoices",
            accounts: accountsByEntityId[entityId] ?? [],
        });
        defaultsByEntityId.set(entityId, resolved);
        return resolved;
    };

    let didChange = false;
    const nextRows = rows.map((row) => {
        if (!row.entityId) {
            return row;
        }

        const defaults = getDefaults(row.entityId);
        const nextAccrualLedger =
            row.accrualLedger || (defaults.accountId ?? "");
        const nextAccrualAccountId =
            row.accrualAccountId || (defaults.accrualAccountId ?? "");

        if (
            nextAccrualLedger === row.accrualLedger &&
            nextAccrualAccountId === row.accrualAccountId
        ) {
            return row;
        }

        didChange = true;
        return {
            ...row,
            accrualLedger: nextAccrualLedger,
            accrualAccountId: nextAccrualAccountId,
        };
    });

    return { rows: didChange ? nextRows : rows, didChange };
}

/**
 * Fills empty account fields with each entity's default accounts (AP/AR and
 * revenue/expense) whenever an entity's chart of accounts loads or any row's
 * entity assignment changes (rows added, removed, or reassigned). Keying on
 * per-row entity IDs instead of every row edit avoids re-filling deliberately
 * cleared account fields.
 */
export function useAccrualDefaultAccountSeeding({
    rows,
    setRows,
    mode,
}: {
    rows: AccrualImporterRow[];
    setRows: React.Dispatch<React.SetStateAction<AccrualImporterRow[]>>;
    mode: AccrualMode;
}) {
    const entityIds = useMemo(() => {
        const uniqueIds = new Set<number>();
        for (const row of rows) {
            if (row.entityId) {
                uniqueIds.add(row.entityId);
            }
        }
        return Array.from(uniqueIds).sort((a, b) => a - b);
    }, [rows]);

    const stableEntityIdsKey = entityIds.join(",");
    const stableEntityIds = useMemoByKeys(
        () => entityIds,
        [stableEntityIdsKey],
    );

    const { accountsByEntityId } = useAccountsByEntity(stableEntityIds);

    // Mode is fixed for the drawer's lifetime, so it is read via ref and
    // intentionally excluded from the effect dependencies.
    const modeRef = useRef(mode);
    modeRef.current = mode;

    const rowEntityIdsKey = rows.map((row) => row.entityId ?? "").join(",");

    useEffect(() => {
        setRows((prevRows) => {
            const { rows: seededRows, didChange } = seedAccrualDefaultAccounts({
                rows: prevRows,
                mode: modeRef.current,
                accountsByEntityId,
            });
            return didChange ? seededRows : prevRows;
        });
    }, [accountsByEntityId, rowEntityIdsKey, setRows]);
}
